// Notification scheduling. Pure time math over sim state: given the clock and
// the incubating eggs, when should the next browser notification fire? The
// browser layer (ui/notify.ts) owns permission and the Notification object;
// this module owns only the "when", so it is testable without a DOM.

import type { EventEgg, GameState } from '../core/types';
import { hourOfDay, MORNING_CHORE_HOUR, EVENING_CHORE_HOUR } from './time';
import { INCUBATION_DAYS } from './eventBirds';

export type NotificationKind = 'morning_chore' | 'evening_chore' | 'hatch';

export interface ScheduledNotification {
  kind: NotificationKind;
  /** Sim-hour at which the notification should fire. */
  atHour: number;
  /** Egg id for hatch notifications. */
  eggId?: string;
}

/** Hours before a hatch the "about to hatch" reminder fires. */
export const HATCH_NOTIFY_LEAD_HOURS = 1;

/** Sim-hour of the next occurrence of a target hour-of-day, at or after simHour. */
export function nextOccurrenceOfHour(simHour: number, targetHour: number): number {
  const day = Math.floor(simHour / 24);
  const current = hourOfDay(simHour);
  if (current <= targetHour) return day * 24 + targetHour;
  return (day + 1) * 24 + targetHour;
}

/** Sim-hour of the next chore window (whichever of morning/evening is sooner). */
export function nextChoreWindow(simHour: number): number {
  const morning = nextOccurrenceOfHour(simHour, MORNING_CHORE_HOUR);
  const evening = nextOccurrenceOfHour(simHour, EVENING_CHORE_HOUR);
  return Math.min(morning, evening);
}

/** The chore kind whose window opens at a given sim-hour, or null. */
export function choreKindAt(atHour: number): NotificationKind | null {
  const h = hourOfDay(atHour);
  if (h === MORNING_CHORE_HOUR) return 'morning_chore';
  if (h === EVENING_CHORE_HOUR) return 'evening_chore';
  return null;
}

/** Sim-hour an event egg finishes incubation. */
export function hatchHourOf(egg: EventEgg): number {
  return egg.incubatingSinceHour! + INCUBATION_DAYS * 24;
}

/**
 * The next egg whose hatch is within the lead window and not yet past, or null
 * when nothing is imminent. Ties break by earliest hatch.
 */
export function nextImminentHatch(
  state: GameState,
  leadHours = HATCH_NOTIFY_LEAD_HOURS,
): { eggId: string; atHour: number } | null {
  let best: { eggId: string; atHour: number } | null = null;
  for (const egg of state.eventEggs ?? []) {
    if (egg.incubatingSinceHour === undefined) continue;
    const hatchAt = hatchHourOf(egg);
    const notifyAt = hatchAt - leadHours;
    if (state.simHour < notifyAt) continue;
    if (state.simHour >= hatchAt) continue;
    if (!best || hatchAt < best.atHour) best = { eggId: egg.id, atHour: hatchAt };
  }
  return best;
}

/**
 * The next notification that should fire at or after the current sim hour. A
 * hatch reminder takes priority over a chore window when both are due, because
 * a hatch is a one-shot event the player cannot re-trigger. Returns null only
 * when the clock is past both chore windows and no egg is imminent.
 */
export function nextNotification(state: GameState): ScheduledNotification | null {
  const choreAt = nextChoreWindow(state.simHour);
  const kind = choreKindAt(choreAt);
  const hatch = nextImminentHatch(state);

  if (hatch) {
    const hatchNotifyAt = hatch.atHour - HATCH_NOTIFY_LEAD_HOURS;
    if (hatchNotifyAt <= choreAt) {
      return { kind: 'hatch', atHour: hatchNotifyAt, eggId: hatch.eggId };
    }
  }

  if (!kind) return null;
  return { kind, atHour: choreAt };
}
